import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export const MIN_PANEL_WIDTH = 320;
export const DEFAULT_PANEL_WIDTH_PERCENTAGE = 0.25;
export const MAX_PANEL_WIDTH_PERCENTAGE = 0.8;

export function getMaxPanelWidth(): number {
  if (globalThis.window === undefined) {
    return MIN_PANEL_WIDTH;
  }

  return Math.max(
    MIN_PANEL_WIDTH,
    Math.round(window.innerWidth * MAX_PANEL_WIDTH_PERCENTAGE)
  );
}

export function getDefaultPanelWidth(): number {
  if (globalThis.window === undefined) {
    return MIN_PANEL_WIDTH;
  }

  const defaultPanelWidth = Math.round(
    window.innerWidth * DEFAULT_PANEL_WIDTH_PERCENTAGE
  );
  return clampPanelWidth(defaultPanelWidth);
}

export function clampPanelWidth(width: number): number {
  return Math.min(getMaxPanelWidth(), Math.max(MIN_PANEL_WIDTH, width));
}

export type UseResizablePanelWidthOptions = {
  enabled?: boolean;
  initialWidth?: number;
  onWidthChange?: (width: number) => void;
};

export type UseResizablePanelWidthResult = {
  isResizing: boolean;
  panelWidth: number;
  setPanelWidth: (width: number) => void;
  startResize: (event: ReactPointerEvent<HTMLElement>) => void;
};

export function useResizablePanelWidth({
  enabled = true,
  initialWidth,
  onWidthChange,
}: UseResizablePanelWidthOptions = {}): UseResizablePanelWidthResult {
  const [panelWidth, setPanelWidthState] = useState(
    () => initialWidth ?? getDefaultPanelWidth()
  );
  const [isResizing, setIsResizing] = useState(false);
  const isResizingRef = useRef(false);
  const onWidthChangeRef = useRef(onWidthChange);
  const stopResizeRef = useRef<(() => void) | null>(null);
  onWidthChangeRef.current = onWidthChange;

  useEffect(() => {
    return () => {
      stopResizeRef.current?.();
    };
  }, []);

  const setPanelWidth = useCallback((width: number) => {
    const clamped = clampPanelWidth(width);
    setPanelWidthState(clamped);
    onWidthChangeRef.current?.(clamped);
  }, []);

  const startResize = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (!enabled || event.button !== 0) {
        return;
      }

      event.preventDefault();
      stopResizeRef.current?.();

      const captureTarget = event.currentTarget;
      const pointerId = event.pointerId;

      isResizingRef.current = true;
      setIsResizing(true);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';

      captureTarget.setPointerCapture(pointerId);

      const handleMove = (moveEvent: PointerEvent) => {
        if (!isResizingRef.current || moveEvent.pointerId !== pointerId) {
          return;
        }

        const nextWidth = window.innerWidth - moveEvent.clientX;
        setPanelWidth(nextWidth);
      };

      const cleanupResize = () => {
        if (!isResizingRef.current) {
          return;
        }

        isResizingRef.current = false;
        setIsResizing(false);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';

        captureTarget.removeEventListener('pointermove', handleMove);
        captureTarget.removeEventListener('pointerup', handlePointerEnd);
        captureTarget.removeEventListener('pointercancel', handlePointerEnd);
        captureTarget.removeEventListener(
          'lostpointercapture',
          handleLostPointerCapture
        );

        try {
          if (captureTarget.hasPointerCapture(pointerId)) {
            captureTarget.releasePointerCapture(pointerId);
          }
        } catch {
          // Pointer capture may already be released.
        }

        stopResizeRef.current = null;
      };

      const handlePointerEnd = (endEvent: PointerEvent) => {
        if (endEvent.pointerId !== pointerId) {
          return;
        }

        cleanupResize();
      };

      const handleLostPointerCapture = (lostEvent: PointerEvent) => {
        if (lostEvent.pointerId !== pointerId) {
          return;
        }

        cleanupResize();
      };

      stopResizeRef.current = cleanupResize;

      captureTarget.addEventListener('pointermove', handleMove);
      captureTarget.addEventListener('pointerup', handlePointerEnd);
      captureTarget.addEventListener('pointercancel', handlePointerEnd);
      captureTarget.addEventListener(
        'lostpointercapture',
        handleLostPointerCapture
      );
    },
    [enabled, setPanelWidth]
  );

  return { isResizing, panelWidth, setPanelWidth, startResize };
}
